<template>
    <flex-container class="brand-copy-embed-textarea" :gap="Size.xs">
        <flex-container
            align-items="center"
            justify-content="end"
            :gap="Size.sm"
        >
            <button-text :icon="IconCopy" @click="copyAllText">
                <text-decorator :size="FontSize.xs">
                    Klik om te kopiëren
                </text-decorator>
                <svg-icon
                    :size="Size.sm"
                    :icon="IconCopy"
                    is-custom-icon
                />
            </button-text>
        </flex-container>

        <label>
            <textarea
                ref="textarea"
                readonly
                class="brand-copy-embed-textarea__input"
                :value="content"
                @focus="selectAllText"
            />
        </label>
    </flex-container>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue';
    import IconCopy from '@keyvue/one-awesome-icons/icons/regular/copy.svg?component';
    import IconClipboardCheck from '@keyvue/one-awesome-icons/icons/regular/clipboard-check.svg?component';
    import { FontSize, NotificationBlock, Size, useNotify } from '@keyvue/one-ui';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';

    const props = defineProps<{
        brandId: string;
    }>();

    const { hasWinckBranding } = useAppBranding();
    const { notify } = useNotify();

    const embedUrl = computed(() => {
        return `${import.meta.env.VITE_WINCK_ASSISTENT_HOST}/embed-assistant.js?brand=${props.brandId}`;
    });

    const content = computed(() => {
        const brand = hasWinckBranding() ? 'Winck' : 'Heyloha';

        return [
            `<!-- ${brand} AI Agent embed code -->`,
            `[script src="${embedUrl.value}"][/script]`,
        ]
            .join('\n')
            .replace(/\[/g, '<')
            .replace(/]/g, '>');
    });

    const textarea = ref<HTMLTextAreaElement>();

    function selectAllText() {
        textarea.value?.select();
    }

    function copyAllText() {
        navigator.clipboard.writeText(textarea.value?.value ?? '');
        notify({
            component: NotificationBlock,
            props: {
                isCustomIcon: true,
                icon: IconClipboardCheck,
                title: 'Gekopieerd naar het klembord',
                color: 'green-500',
            },
            duration: 4000,
        });
    }
</script>

<style scoped lang="scss">
    .brand-copy-embed-textarea {
        border-radius: size(xs);
        background: color(gray-100);
        position: relative;

        label, textarea {
            display: block;
            width: 100%;
        }

        label {
            background-color: var(--theme-colors-default--gray-50);
            padding: size(xs);
            border-radius: size(xs);
            border: 1px solid var(--theme-colors-default--gray-200);
        }

        textarea {
            color: var(--theme-colors-default-gray-300);

            &:focus {
                outline: none;
            }
        }

        &__input {
            background-color: transparent;
            min-height: 5rem;
            border: none;
            resize: none;
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            font-family: monospace;
        }
    }
</style>
